<template>
  <Teleport to="body">
    <Transition name="modal">
      <div
        v-if="isOpen && claim"
        class="fixed inset-0 z-50 overflow-y-auto"
        @click.self="$emit('close')"
      >
        <!-- Backdrop -->
        <div class="fixed inset-0 bg-black bg-opacity-50 transition-opacity"></div>

        <!-- Modal -->
        <div class="flex min-h-full items-center justify-center p-4">
          <div class="relative border border-gray-200 dark:border-gray-700 bg-white dark:bg-black rounded-lg shadow-xl max-w-md w-full">
            <!-- Header -->
            <div class="bg-green-50 dark:bg-green-900/20 border-b border-green-200 dark:border-green-800 px-6 py-4 flex items-center">
              <div class="flex-shrink-0 w-10 h-10 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center mr-3">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6 text-green-600 dark:text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h3 class="text-lg font-semibold text-green-900 dark:text-green-100">
                Approve Claim
              </h3>
            </div>

            <!-- Content -->
            <div class="px-6 py-6">
              <p class="text-gray-700 dark:text-gray-300 mb-4">
                Are you sure you want to approve this claim?
              </p>

              <!-- Claim Details -->
              <div class="bg-gray-50 dark:bg-black rounded-lg p-4 space-y-2">
                <div class="flex justify-between">
                  <span class="text-sm text-gray-500 dark:text-gray-400">Claim Number:</span>
                  <span class="text-sm font-medium text-gray-900 dark:text-white">{{ claim.claim_number }}</span>
                </div>
                <div class="flex justify-between">
                  <span class="text-sm text-gray-500 dark:text-gray-400">Customer:</span>
                  <span class="text-sm font-medium text-gray-900 dark:text-white">
                    {{ claim.user?.first_name }} {{ claim.user?.last_name }}
                  </span>
                </div>
                <div class="flex justify-between">
                  <span class="text-sm text-gray-500 dark:text-gray-400">Email:</span>
                  <span class="text-sm font-medium text-gray-900 dark:text-white">
                    {{ claim.user?.email }}
                  </span>
                </div>
                <div class="flex justify-between">
                  <span class="text-sm text-gray-500 dark:text-gray-400">Tire:</span>
                  <span class="text-sm font-medium text-gray-900 dark:text-white">
                    {{ claim.tire?.pattern }} - {{ claim.tire?.tire_size }}
                  </span>
                </div>
              </div>

              <div class="mt-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
                <div class="flex items-start space-x-2">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  <div>
                    <p class="text-sm text-green-800 dark:text-green-300">
                      <strong>Email Notification:</strong> An approval email will be sent to <strong>{{ claim.user?.email }}</strong> with claim details and next steps.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <!-- Footer -->
            <div class="bg-gray-50 dark:bg-black border-t border-gray-200 dark:border-gray-700 px-6 py-4 flex items-center justify-end space-x-3">
              <button
                @click="$emit('close')"
                :disabled="loading"
                class="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                @click="$emit('confirm')"
                :disabled="loading"
                class="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors duration-200 flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <svg v-if="loading" class="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                  <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <svg v-else xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                </svg>
                <span>{{ loading ? 'Approving...' : 'Approve Claim' }}</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import type { Claim } from '@/types/claim';

defineProps<{
  isOpen: boolean;
  claim: Claim | null;
  loading?: boolean;
}>();

defineEmits<{
  close: [];
  confirm: [];
}>();
</script>

<style scoped>
.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.3s ease;
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}
</style>

